import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  TableFooter,
  TablePagination,
} from '@material-ui/core';
import { format } from 'date-fns-tz';
import { ptBR } from 'date-fns/locale';
import { LatLngTuple } from 'leaflet';
import FilterBar, { IncFilterOpsType } from '../../components/FilterBar';
import { useStyles } from './styles';
import { jsonb as JsonbType } from '../../@types/jsonb';
import { DrawPolygonType } from '../../components/MapContent';
import Colors from '../../components/Colors';

import { fetchPaginatedIncidents } from '../../actions/incidentActions';
import { getFilterDates } from '../../actions/managementActions';
import { IncidentResponsePaginated } from '../../@types/incidentTypes';

const initialRecordsResponse: IncidentResponsePaginated = {
  count: 0,
  next: '',
  previous: '',
  results: [],
};

const initialJsonb: JsonbType = {
  jsonb: {
    driverVeiculo: {
      'Tipo Veículo': {
        _rule_type: 'containment_multiple',
        contains: [],
      },
    },
    driverVitima: {
      'Gravidade da Lesão': {
        _rule_type: 'containment_multiple',
        contains: [],
      },
    },
    driverIncidenteDetails: {
      Natureza: {
        _rule_type: 'containment',
        contains: [],
      },
      Severidade: {
        _rule_type: 'containment',
        contains: [],
      },
    },
  },
};

const initialFilterOps: IncFilterOpsType = {
  endDate: '',
  startDate: '',
  veiculos: [],
  gravidades: [],
  location: [],
  tipoAcidentes: [],
  bairros: '',
  vias: '',
  regionais: '',
  drawPolygon: {},
};

// Carregando os Sinistros de Acordo com a Data no LocalStorage e Transforma cada item em um Jsonb
const loadJsonbStorage = (): JsonbType => {
  // Carrega os 14 Sinistros
  const loadedFilterOps = localStorage.getItem('incFilterOps');
  let jsonbObj: JsonbType = initialJsonb;

  if (loadedFilterOps) {
    const parsedFilters: IncFilterOpsType = JSON.parse(loadedFilterOps);

    const vehicles: Array<string | string[]> = [];
    const gravity: Array<string | string[]> = [];
    const nature: Array<string | string[]> = [];

    parsedFilters.veiculos.map((item) => {
      vehicles.push(item);
      vehicles.push([item]);
    });

    parsedFilters.gravidades.map((item) => {
      gravity.push(item);
      gravity.push([item]);
    });

    parsedFilters.tipoAcidentes.map((item) => {
      nature.push(item);
      nature.push([item]);
    });

    jsonbObj = {
      jsonb: {
        driverVeiculo: {
          'Tipo Veículo': {
            _rule_type: 'containment_multiple',
            contains: vehicles,
          },
        },
        driverIncidenteDetails: {
          Natureza: {
            _rule_type: 'containment',
            contains: nature,
          },
          Severidade: {
            _rule_type: 'containment',
            contains: gravity,
          },
        },
      },
    };
    // console.log(jsonbObj);
    return jsonbObj;
  }
  return jsonbObj;
};

const loadFilterOps = (): IncFilterOpsType => {
  let filterOpsObj: IncFilterOpsType = initialFilterOps;
  const loadedFilterOps = localStorage.getItem('incFilterOps');

  // Carrega a filtragem do Local Storage e armazena no filterOps
  if (loadedFilterOps) {
    filterOpsObj = JSON.parse(loadedFilterOps);
  }

  return filterOpsObj;
};

const RegistersIncident: React.FC = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(true);
  // Armazenando
  const [records, setRecords] = useState<IncidentResponsePaginated>(
    initialRecordsResponse,
  );

  const [dateMin, setDateMin] = useState<string>(
    `${process.env.REACT_APP_MAP_START_DEFAULT_DATE}`,
  );
  const [dateMax, setDateMax] = useState<string>(
    `${process.env.REACT_APP_MAP_END_DEFAULT_DATE}`,
  );

  // Armazenando todos os Jsonb carregados na variavel jsonb
  const [jsonb, setJsonb] = useState<JsonbType>(loadJsonbStorage());
  const [address, setAddress] = useState<string>('');
  const [requester, setRequester] = useState<string>('');
  const [tablePage, setTablePage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [pageOffset, setPageOffset] = useState<number>(0);
  const [position, setPosition] = useState<LatLngTuple>([0, 0]);

  const [zoom, setZoom] = useState<number>(0);
  const [boundaryPolygon, setBoundaryPolygon] = useState<{}>({
    polygon_id: loadFilterOps().bairros,
  });
  const [bboxPolygon, setBboxPolygon] = useState<{}>({});

  const [admin, setAdmin] = useState<boolean>(false);
  const [typist, setTypist] = useState<boolean>(false);
  const [tecEng, setTecEng] = useState<boolean>(false);
  const [geofi, setGeofi] = useState<boolean>(false);
  const [publicGroup, setPublicGroup] = useState<boolean>(false);
  const [startDateMap, setStartDateMap] = useState<string>('2014-01-01T00:00:00Z');
  const [endDateMap, setEndDateMap] = useState<string>('2020-06-30T23:59:59.999000Z');
  const [loadingDate, setloadingDate] = useState<boolean>(true);
  const [drawPolygon, setDrawPolygon] = useState<DrawPolygonType | {}>(
    loadFilterOps().drawPolygon,
  );

  // Pegando as Opções de Filtragem e Amarzenando na FilterOps
  const filterOps: IncFilterOpsType = loadFilterOps();

  const params = useMemo(() => {
    return {
      archived: 'False',
      details_only: 'True',
      occurred_min: filterOps.startDate || dateMin,
      occurred_max: filterOps.endDate || dateMax,
      ...jsonb,
      ...boundaryPolygon,
      ...drawPolygon,
    };
  }, [
    filterOps.startDate,
    filterOps.endDate,
    dateMin,
    dateMax,
    jsonb,
    boundaryPolygon,
    drawPolygon,
  ]);

  useEffect(() => {
    const groups = localStorage.getItem('groups')?.split(' ');
    if (groups) {
      groups.forEach((gr: string) => {
        if (gr === 'public') {
          setPublicGroup(true);
        } else if (gr === 'digitador') {
          setTypist(true);
        } else if (gr === 'tec_engenharia') {
          setTecEng(true);
        } else if (gr === 'geofi') {
          setGeofi(true);
        } else if (gr === 'admin') {
          setAdmin(true);
        }
      });
    }
  }, []);

  const fetchManagementDate = useCallback(async () => {
    setloadingDate(true);
    try {
      const dates = await getFilterDates(filterOps.startDate, filterOps.endDate);
      setDateMin(dates.dateMin);
      setDateMax(dates.dateMax);
      setStartDateMap(dates.startDateMap);
      setEndDateMap(dates.endDateMap);
    } catch (error) {
      console.log(error);
    } finally {
      setloadingDate(false);
    }
  }, [filterOps.endDate, filterOps.startDate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await fetchPaginatedIncidents({
        params: {
          ...params,
          limit: rowsPerPage,
          offset: pageOffset,
        },
      });
      setRecords(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [
    rowsPerPage,
    dateMin,
    dateMax,
    jsonb,
    pageOffset,
    boundaryPolygon,
    drawPolygon,
    filterOps.endDate,
    filterOps.startDate,
  ]);

  useEffect(() => {
    fetchManagementDate();
  }, [fetchManagementDate]);

  // Após Carregar as Datas, efetua a requisição de coletar o dados
  useEffect(() => {
    if (!loadingDate) {
      fetchData();
    }
  }, [loadingDate, fetchData]);

  const handleChangePage = (event: unknown, newPage: number) => {
    newPage < tablePage
      ? setPageOffset((prev) => prev - rowsPerPage)
      : setPageOffset((prev) => prev + rowsPerPage);

    setTablePage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPageOffset(0);
    setTablePage(0);
  };

  return (
    <>
      {/* FilterBar passando todos os parâmetros de filtragem ao Jsonb */}
      <FilterBar
        filterJson={setJsonb}
        dateMaxSet={setDateMax}
        dateMinSet={setDateMin}
        setPositionSearch={setPosition}
        zoom={setZoom}
        id_boundary={setBoundaryPolygon}
        bbox_boundary={setBboxPolygon}
        routeTo="sinistro"
        startDateMap={startDateMap}
        endDateMap={endDateMap}
        drawPolygon={drawPolygon}
        setDrawPolygon={setDrawPolygon}
        address={address}
        setAddress={setAddress}
        requester={requester}
        setRequester={setRequester}
        onFilter={() => {
          setTablePage(0);
          setPageOffset(0);
        }}
      />
      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress className={classes.loadingSpinner} size={60} thickness={5} />
        </div>
      ) : (
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell
                  className={classes.responsive}
                  style={{ color: Colors.tertiary }}
                >
                  Data e Hora
                </TableCell>
                <TableCell style={{ color: Colors.tertiary }}>Endereço 1</TableCell>
                <TableCell style={{ color: Colors.tertiary }}>Endereço 2</TableCell>
                <TableCell
                  className={classes.responsive}
                  style={{ color: Colors.tertiary }}
                >
                  Natureza
                </TableCell>
                <TableCell
                  className={classes.responsive}
                  style={{ color: Colors.tertiary }}
                >
                  Jurisdição
                </TableCell>
                <TableCell
                  className={classes.responsive}
                  style={{ color: Colors.tertiary }}
                >
                  Criador do registro
                </TableCell>
                <TableCell style={{ color: Colors.tertiary }}>Funções</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {records.results.map((value) => (
                <TableRow key={value.uuid} hover>
                  <TableCell className={classes.responsive}>
                    {`${format(new Date(value.occurred_from), 'dd MMMM, yyyy, HH:mm', {
                      locale: ptBR,
                    })}`}
                  </TableCell>
                  <TableCell>{value.data.driverIncidenteDetails['Endereço 1']}</TableCell>
                  <TableCell>{value.data.driverIncidenteDetails['Endereço 2']}</TableCell>
                  <TableCell className={classes.responsive}>
                    {value.data.driverIncidenteDetails.Natureza}
                  </TableCell>
                  <TableCell className={classes.responsive}>
                    {value.data.driverIncidenteDetails.Jurisdição}
                  </TableCell>
                  <TableCell className={classes.responsive}>{value.created_by}</TableCell>
                  <TableCell>
                    <div className={classes.buttonsCol}>
                      {(publicGroup || geofi || tecEng) && !(admin || typist) ? null : (
                        <a
                          href={`/editar/sinistro/${value.uuid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: 'black' }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            className={classes.button}
                          >
                            Editar
                          </Button>
                        </a>
                      )}
                      <a
                        href={`/registro/sinistro/${value.uuid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', color: 'black' }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          className={classes.button}
                        >
                          Exibir
                        </Button>
                      </a>
                      {(publicGroup || geofi) && !(admin || typist || tecEng) ? (
                        <></>
                      ) : (
                        <a
                          href={`/historico/${value.uuid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: 'black' }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            className={classes.button}
                          >
                            Histórico
                          </Button>
                        </a>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                {/* Quantidade de itens na página */}
                <TablePagination
                  rowsPerPageOptions={[25, 50]}
                  rowsPerPage={rowsPerPage}
                  count={records.count}
                  page={tablePage}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                  }}
                  labelRowsPerPage={<span>Linhas por página</span>}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count}`
                  }
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default RegistersIncident;
